// src/socket.js
import { io } from 'socket.io-client';

const ENDPOINT = process.env.REACT_APP_SOCKET;
export let socket = null;
let reconnectTimer = null;
let storedUserData = null; // Store user data for reconnection

const MAX_RECONNECTION_ATTEMPTS = 50;
const RECONNECTION_DELAY = 1000;

// Registry to store listeners so they can be re-attached when socket reconnects
const registeredListeners = [];

/**
 * Check if socket is connected and ready
 * @returns {boolean}
 */
const isSocketReady = () => {
    return socket !== null && socket.connected;
};

/**
 * Initialize the socket connection with user data.
 */
export const initiateSocketConnection = (userData) => {
    if (socket) {
        console.warn('Socket is already connected.');
        return;
    }

    // Store user data for reconnection (only if provided)
    if (userData) {
        storedUserData = userData;
    }

    // Use stored user data if no userData provided (reconnection scenario)
    const authData = userData || storedUserData;

    // Cannot connect without user data
    if (!authData || !authData.userId || !authData.username || !authData.email) {
        console.error('Cannot initialize socket: missing required user data (userId, username, email)');
        return;
    }

    // Clear any existing reconnect timer
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }

    try {
        socket = io(ENDPOINT, {
            auth: {...authData },
            reconnection: true,
            reconnectionAttempts: MAX_RECONNECTION_ATTEMPTS,
            reconnectionDelay: RECONNECTION_DELAY,
        });

        socket.on('connect', () => {
            console.log('Connected to the socket server');
            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }
            
            // Re-bind any previously registered listeners.
            // Guard against `socket` having been nulled by a concurrent
            // disconnect/reconnect (was throwing "Cannot read properties of null").
            registeredListeners.forEach(({ event, callback }) => {
                if (socket) socket.on(event, callback);
            });
        });

        socket.on('connect_error', (err) => {
            console.error('Connection error:', err);
            handleDisconnection();
        });

        socket.on('user_language_updated', (data) => {
            console.log('🔌 User language updated:', data);
        });

        socket.on('disconnect', () => {
            console.log('Disconnected from the socket server');
            handleDisconnection();
        });

        socket.on('handshake', (data) => {
            console.log('Received handshake from server:', data);
            if (isSocketReady()) {
                socket.emit('handshake_ack', { message: 'pong' });
            }
        });
    } catch (error) {
        console.error('Error initializing socket:', error);
        handleDisconnection();
    }
};

/**
 * Handle socket disconnection and reconnection attempts
 */
const handleDisconnection = () => {
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
    }

    socket = null;

    // Only attempt to reconnect if we have stored user data
    if (storedUserData) {
        reconnectTimer = setTimeout(() => {
            if (!isSocketReady()) {
                console.log('Attempting to reconnect...');
                initiateSocketConnection(); // Will use storedUserData
            }
        }, RECONNECTION_DELAY);
    } else {
        console.warn('Cannot reconnect: no stored user data available');
    }
};

/**
 * Generic event listener setup with connection check
 * Stores listeners in registry so they can be re-attached when socket reconnects
 * @param {string} event - Event name to listen for
 * @param {Function} callback - Callback function
 * @returns {Function} Cleanup function
 */
const createEventListener = (event, callback) => {
    // Always add to registry so it can be attached when socket connects
    const registryItem = { event, callback };
    registeredListeners.push(registryItem);
    
    // If socket is ready, attach immediately
    if (socket) {
        socket.on(event, callback);
    }
    
    // Return cleanup function that removes from both socket and registry
    return () => {
        if (socket) {
            socket.off(event, callback);
        }
        const index = registeredListeners.indexOf(registryItem);
        if (index > -1) {
            registeredListeners.splice(index, 1);
        }
    };
};

// Update all event listeners to use the new generic function
export const onUserConnected = (callback) =>
    createEventListener('user_connected', callback);

export const onUserDisconnected = (callback) =>
    createEventListener('user_disconnected', callback);

export const onUserListReceived = (callback) =>
    createEventListener('user_list', callback);

export const onMessageReceived = (callback) =>
    createEventListener('receive_message', callback);

export const onPrivateMessageReceived = (callback) =>
    createEventListener('receive_private_message', callback);

export const onExpertOnlineStatusReceived = (callback) =>
    createEventListener('expert_online_status', callback);

export const onVideoCallStarted = (callback) =>
    createEventListener('receive_call_event', callback);

export const checkExpertOnlineStatus = (expertIds) => {
    if (!socket) {
        console.error('socket is not init')
        return () => {};
    }
    socket.emit('check_expert_online_status', expertIds);
    return () => socket.off('check_expert_online_status');
}

export const onObserverMessageRecived = (callback) => {
    if (!socket) {
        console.error('socket is not init')
        return () => {};
    }

    socket.on('receive_observer_message', callback);
    return () => socket.off('recived_observer_message', callback);
}

/**
 * Send a private message with connection check
 */
export const sendPrivateMessage = ({
    threadId,
    type,
    content,
    receiverId,
    time,
    fileName,
    fileType,
    fileData,
    senderId,
}) => {
    if (!isSocketReady()) {
        console.error('Socket is not connected. Message not sent.');
        // You might want to queue the message for retry or handle the error
        return false;
    }

    try {
        socket.emit('send_private_message', {
            threadId,
            type,
            content,
            receiverId,
            time,
            fileName,
            fileType,
            fileData,
            senderId,
        });
        return true;
    } catch (error) {
        console.error('Error sending private message:', error);
        return false;
    }
};

/**
 * Send a conclude message with connection check
 */
export const sendConcludeMessage = ({ threadId, type, content, receiverId, senderId, time }) => {
    if (!isSocketReady()) {
        console.error('Socket is not connected. Conclude message not sent.');
        return false;
    }

    try {
        socket.emit('send_conclude_message', {
            threadId,
            type,
            content,
            receiverId,
            senderId,
            time
        });
        return true;
    } catch (error) {
        console.error('Error sending conclude message:', error);
        return false;
    }
};

export const onUserLanguageUpdated = (callback) => {
    if (!socket) {
        console.error('socket is not init')
        return () => {};
    }
    socket.on('user_language_updated', callback);
    return () => socket.off('user_language_updated', callback);
}

export const updateUserLanguage = (language) => {
    if (!isSocketReady()) {
        console.error('❌ Socket not ready for sending message');
        return false;
    }

    if (!socket) {
        console.error('socket is not init')
        return;
    }
    socket.emit('update_user_language', language);
}

/**
 * Request user list with connection check
 */
export const sendRequestUserList = () => {
    if (!isSocketReady()) {
        console.error('Socket is not connected. Cannot request user list.');
        return false;
    }

    try {
        socket.emit('request_user_list');
        console.log('Emitted request_user_list');
        return true;
    } catch (error) {
        console.error('Error requesting user list:', error);
        return false;
    }
};

/**
 * Safely disconnect the socket
 */
export const disconnectSocket = () => {
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }

    if (socket) {
        socket.disconnect();
        socket = null;
    }
    
    // Clear stored user data on explicit disconnect
    storedUserData = null;
};